'use client';

import { useEffect, useState } from 'react';
import { TagKind } from '@/application/tag/tag.types';
import { Button } from '@/atoms/Button/Button';
import { Container } from '@/atoms/Container/Container';
import { Popover, PopoverContent, PopoverTrigger } from '@/atoms/Popover/Popover';
import { mergeTaggerIds, useEntityTaggers } from '@/hooks/useEntityTaggers/useEntityTaggers';
import { useIsMobile } from '@/hooks/useIsMobile/useIsMobile';
import { canonicalizeTagLabel } from '@/libs/utils/utils';
import { useAuthStore } from '@/stores/auth/auth.store';
import { WhoTaggedExpandedList } from '../WhoTaggedExpandedList/WhoTaggedExpandedList';
import { MAX_VISIBLE_AVATARS, POPOVER_HOVER_DELAY } from './PostTagPopoverWrapper.constants';
import type { PostTagPopoverWrapperProps } from './PostTagPopoverWrapper.types';
import { TaggerAvatar } from './TaggerAvatar/TaggerAvatar';

/**
 * PostTagPopoverWrapper
 *
 * Wraps a tag with hover tooltip showing tagger avatars.
 * Click on avatar opens user profile popover.
 * Click on overflow counter (+N) opens full taggers list.
 *
 * Note: Hover tooltip is desktop-only to avoid unintended
 * popover activation on mobile tap interactions.
 */
export function PostTagPopoverWrapper({
  taggers,
  taggersCount,
  postId,
  tagLabel,
  children,
}: PostTagPopoverWrapperProps) {
  const [open, setOpen] = useState(false);
  const [showAllTaggers, setShowAllTaggers] = useState(false);
  const isMobile = useIsMobile();
  const viewerId = useAuthStore((state) => state.currentUserPubky);
  const { taggerStates, loadTaggers, loadMoreTaggers } = useEntityTaggers(tagLabel ? postId : null, TagKind.POST);
  const previewTaggerIds = taggers.map((tagger) => tagger.id);
  const taggerState = tagLabel ? taggerStates.get(canonicalizeTagLabel(tagLabel)) : undefined;
  const isFetching = taggerState?.isLoading ?? false;

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    // When hover popover closes, also close the "all taggers" dropdown
    if (!newOpen) {
      setShowAllTaggers(false);
    }
  };

  useEffect(() => {
    if (!showAllTaggers || !postId || !tagLabel) return;
    void loadTaggers(tagLabel, taggersCount);
  }, [showAllTaggers, postId, tagLabel, taggersCount, loadTaggers]);

  // On mobile or when no taggers, just render children without popover
  if (isMobile || (taggers.length === 0 && taggersCount === 0)) {
    return <>{children}</>;
  }

  const visibleTaggers = taggers.slice(0, MAX_VISIBLE_AVATARS);
  const overflowCount = Math.max(0, taggersCount - visibleTaggers.length);

  return (
    <Popover hover hoverDelay={POPOVER_HOVER_DELAY} open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="bottom"
        sideOffset={4}
        align="start"
        className="w-auto border-none bg-transparent p-0 shadow-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <Container overrideDefaults className="flex items-center">
          {visibleTaggers.map((tagger, index) => (
            <TaggerAvatar key={tagger.id} tagger={tagger} index={index} />
          ))}
          {overflowCount > 0 && (
            <Popover open={showAllTaggers} onOpenChange={setShowAllTaggers}>
              <PopoverTrigger asChild>
                <Button
                  overrideDefaults
                  className="z-0 -ml-2 flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium shadow-sm transition-opacity hover:opacity-80"
                  aria-label={`Show all ${taggersCount} taggers`}
                >
                  +{overflowCount}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                sideOffset={8}
                align="start"
                className="w-auto p-0"
                onOpenAutoFocus={(e) => e.preventDefault()}
              >
                {showAllTaggers && (
                  <WhoTaggedExpandedList
                    taggerIds={mergeTaggerIds({
                      fetchedIds: taggerState?.hasFetched ? taggerState.ids : undefined,
                      previewIds: previewTaggerIds,
                      viewerId,
                      isViewerTagger: taggerState?.isViewerTagger,
                    })}
                    fallbackTaggers={taggers}
                    isLoadingTaggers={isFetching && !taggerState?.hasFetched}
                    isLoadingMore={isFetching && taggerState?.hasFetched}
                    hasMore={taggerState?.hasMore}
                    hasError={taggerState?.hasError}
                    onLoadMore={() => void loadMoreTaggers(tagLabel ?? '')}
                  />
                )}
              </PopoverContent>
            </Popover>
          )}
        </Container>
      </PopoverContent>
    </Popover>
  );
}
